/**
 * AdminSection - 센터 관리자 관리 섹션
 *
 * 관리자 목록 표시, 초대, 제거 기능을 제공한다.
 * CenterDetailPage에 포함되며, 센터 소유자만 사용 가능하다.
 */

import { useState, useEffect } from "react";
import { useListAdmins, useInviteAdmin, useRemoveAdmin } from "@/hooks/useAdminManagement";

interface AdminSectionProps {
  centerId: number;
  centerName: string;
}

export function AdminSection({ centerId, centerName }: AdminSectionProps) {
  const { data, isLoading, refetch } = useListAdmins(centerId);
  const inviteMutation = useInviteAdmin();
  const removeMutation = useRemoveAdmin();

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [invitePassword, setInvitePassword] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [removeTargetUid, setRemoveTargetUid] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // 모달 열릴 때 폼 초기화
  useEffect(() => {
    if (isInviteModalOpen) {
      setInviteEmail("");
      setInviteName("");
      setInvitePassword("");
      setInviteError(null);
      inviteMutation.reset();
    }
  }, [isInviteModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);

    try {
      const result = await inviteMutation.mutateAsync({
        email: inviteEmail.trim(),
        name: inviteName.trim(),
        initialPassword: invitePassword,
      });

      if (!result.success && result.error) {
        setInviteError(result.error.message);
        return;
      }

      setIsInviteModalOpen(false);
      refetch();
    } catch (err: unknown) {
      setInviteError(err instanceof Error ? err.message : "초대에 실패했습니다.");
    }
  };

  const handleRemove = async (adminUid: string) => {
    setRemoveError(null);

    try {
      const result = await removeMutation.mutateAsync({ adminUid });

      if (!result.success && result.error) {
        setRemoveError(result.error.message);
        return;
      }

      setRemoveTargetUid(null);
      refetch();
    } catch (err: unknown) {
      setRemoveError(err instanceof Error ? err.message : "제거에 실패했습니다.");
    }
  };

  const admins = data?.admins ?? [];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">관리자 관리</h2>
        <button
          onClick={() => setIsInviteModalOpen(true)}
          className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
        >
          관리자 초대
        </button>
      </div>

      {/* 관리자 목록 */}
      <div className="mt-4">
        {isLoading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          </div>
        ) : admins.length === 0 ? (
          <p className="text-sm text-gray-500">초대된 관리자가 없습니다.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {admins.map((admin) => (
              <li key={admin.uid} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">{admin.name}</p>
                  {admin.email && <p className="text-sm text-gray-500">{admin.email}</p>}
                  <p className="text-xs text-gray-400">
                    가입일: {new Date(admin.joinedAt).toLocaleDateString("ko-KR")}
                  </p>
                </div>
                <button
                  onClick={() => setRemoveTargetUid(admin.uid)}
                  className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                >
                  제거
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 관리자 초대 모달 */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">관리자 초대</h3>
            <p className="text-sm text-gray-500 mt-1">{centerName}에 관리자를 초대합니다.</p>

            <form onSubmit={(e) => void handleInvite(e)} className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="홍길동"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  초기 비밀번호
                </label>
                <input
                  type="password"
                  value={invitePassword}
                  onChange={(e) => setInvitePassword(e.target.value)}
                  placeholder="6자 이상"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {inviteError && <p className="text-sm text-red-600">{inviteError}</p>}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsInviteModalOpen(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={
                    !inviteEmail.trim() ||
                    !inviteName.trim() ||
                    !invitePassword ||
                    inviteMutation.isPending
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviteMutation.isPending ? "초대 중..." : "초대"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 관리자 제거 확인 모달 */}
      {removeTargetUid && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">관리자 제거</h3>
            <p className="text-sm text-gray-600 mt-2">
              이 관리자를 {centerName}에서 제거하시겠습니까? 관리자의 계정은 삭제되지 않습니다.
            </p>

            {removeError && <p className="text-sm text-red-600 mt-2">{removeError}</p>}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setRemoveTargetUid(null);
                  setRemoveError(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                취소
              </button>
              <button
                onClick={() => void handleRemove(removeTargetUid)}
                disabled={removeMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {removeMutation.isPending ? "처리 중..." : "제거"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
